import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Box,
  TextField,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import axios from '../../api/client';
import { useCurrency } from '../../context/CurrencyContext';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import ScrollTable from '../ScrollTable';
import TableToolbar from '../TableToolbar';
import useTableSearch from '../../hooks/useTableSearch';


const TenantLedger = ({ tenantId, tenantName }) => {
  const [ledger, setLedger] = useState([]);

  // Client-side: this screen loads every row, so filtering here searches all of
  // them. Screens that page on the server search there instead.
  const {
    query, setQuery, filterValues, setFilter, reset,
    rows: visibleLedger, resultCount, totalCount
  } = useTableSearch(ledger, {
    searchFields: ['description', 'entryType', 'notes'],
    filters: {
      entryType: (row, v) => row.entryType === v
    }
  });
  const [balance, setBalance] = useState(0);
  const [creditPool, setCreditPool] = useState({ total: 0, payments: [] });
  const { formatMoney: formatCurrency } = useCurrency();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [adjustmentOpen, setAdjustmentOpen] = useState(false);
  const [adjustmentForm, setAdjustmentForm] = useState({
    amount: 0,
    type: 'adjustment',
    description: ''
  });

  useEffect(() => {
    if (tenantId) {
      fetchLedger();
      fetchBalance();
      fetchCreditPool();
    }
    // Keyed on the tenant and the date range, which is everything these three read. They
    // are declared below and rebuilt every render, so naming them would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, startDate, endDate]);

  const fetchLedger = async () => {
    try {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const response = await axios.get(`/tenant-ledger/${tenantId}`, { params });
      setLedger(response.data || []);
    } catch (error) {
      toast.error('Error fetching ledger');
    }
  };

  const fetchBalance = async () => {
    try {
      const response = await axios.get(`/tenant-ledger/${tenantId}/balance`);
      setBalance(response.data.balance || 0);
    } catch (error) {
      console.error('Error fetching balance:', error);
    }
  };

  const fetchCreditPool = async () => {
    try {
      const response = await axios.get(`/tenant-ledger/${tenantId}/credit-pool`);
      setCreditPool(response.data || { total: 0, payments: [] });
    } catch (error) {
      console.error('Error fetching credit pool:', error);
    }
  };

  const handleAdjustment = async () => {
    try {
      await axios.post(`/tenant-ledger/${tenantId}/adjustment`, adjustmentForm);
      toast.success('Adjustment created');
      setAdjustmentOpen(false);
      setAdjustmentForm({ amount: 0, type: 'adjustment', description: '' });
      fetchLedger();
      fetchBalance();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error creating adjustment');
    }
  };


  const getEntryTypeColor = (type) => {
    const colors = {
      invoice: 'error',
      payment: 'success',
      allocation: 'info',
      late_fee: 'warning',
      discount: 'success',
      adjustment: 'default',
      // Shown but never counted — a deposit row carries no debit or credit.
      security: 'info'
    };
    return colors[type] || 'default';
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">
          {tenantName ? `${tenantName} - Ledger` : 'Tenant Ledger'}
        </Typography>
        <Button 
          variant="outlined" 
          startIcon={<AddIcon />}
          onClick={() => setAdjustmentOpen(true)}
        >
          Add Adjustment
        </Button>
      </Box>

      {/* Balance Summary */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, bgcolor: balance > 0 ? 'error.light' : balance < 0 ? 'success.light' : 'grey.100' }}>
            <Typography variant="subtitle2" color="textSecondary">Current Balance</Typography>
            <Typography variant="h5" fontWeight="bold">
              {formatCurrency(Math.abs(balance))}
            </Typography>
            <Typography variant="caption">
              {balance > 0 ? 'Amount Owed' : balance < 0 ? 'Credit Balance' : 'Paid Up'}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, bgcolor: 'info.light' }}>
            <Typography variant="subtitle2" color="textSecondary">Unallocated Credit</Typography>
            <Typography variant="h5" fontWeight="bold">
              {formatCurrency(creditPool.total || 0)}
            </Typography>
            <Typography variant="caption">
              {creditPool.payments?.length || 0} payment(s) available
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="textSecondary">Total Entries</Typography>
            <Typography variant="h5" fontWeight="bold">
              {ledger.length}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Date Filters */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Button 
            variant="outlined" 
            onClick={() => { setStartDate(''); setEndDate(''); }}
            fullWidth
          >
            Clear Filters
          </Button>
        </Grid>
      </Grid>

      {/* Credit Pool Alert */}
      {creditPool.total > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="subtitle2">Unallocated Credit Available</Typography>
          <Typography>
            {formatCurrency(creditPool.total)} in credit pool. This will be automatically applied to future invoices.
          </Typography>
        </Alert>
      )}

      {/* Ledger Table */}
      <TableToolbar
        search={query}
        onSearchChange={setQuery}
        searchPlaceholder="Description or type"
        onReset={reset}
        resultCount={resultCount}
        totalCount={totalCount}
        filters={[
          { key: 'entryType', label: 'Entry type', value: filterValues.entryType || '',
            onChange: (v) => setFilter('entryType', v), minWidth: 200,
            options: [...new Set(ledger.map((e) => e.entryType))].filter(Boolean)
              .map((t) => ({ value: t, label: t.replace(/_/g, ' ') })) }
        ]}
      />
      <ScrollTable>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="right">Debit</TableCell>
              <TableCell align="right">Credit</TableCell>
              <TableCell align="right">Balance</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleLedger.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No ledger entries found
                </TableCell>
              </TableRow>
            ) : (
              visibleLedger.map((entry) => (
                <TableRow key={entry._id}>
                  <TableCell>
                    {format(new Date(entry.entryDate), 'MMM dd, yyyy')}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={entry.entryType}
                      color={getEntryTypeColor(entry.entryType)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{entry.description}</TableCell>
                  <TableCell align="right">
                    {entry.debit > 0 ? (
                      <Typography color="error" fontWeight="bold">
                        {formatCurrency(entry.debit)}
                      </Typography>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {entry.credit > 0 ? (
                      <Typography color="success.main" fontWeight="bold">
                        {formatCurrency(entry.credit)}
                      </Typography>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      color={entry.runningBalance > 0 ? 'error' : entry.runningBalance < 0 ? 'success.main' : 'textPrimary'}
                    >
                      {formatCurrency(entry.runningBalance)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </ScrollTable>

      {/* Adjustment Dialog */}
      <Dialog open={adjustmentOpen} onClose={() => setAdjustmentOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Adjustment</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="Adjustment Type"
                value={adjustmentForm.type}
                onChange={(e) => setAdjustmentForm({ ...adjustmentForm, type: e.target.value })}
              >
                <MenuItem value="late_fee">Late Fee</MenuItem>
                <MenuItem value="penalty">Penalty</MenuItem>
                <MenuItem value="discount">Discount</MenuItem>
                <MenuItem value="waiver">Waiver</MenuItem>
                <MenuItem value="adjustment">General Adjustment</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Amount"
                type="number"
                value={adjustmentForm.amount}
                onChange={(e) => setAdjustmentForm({ ...adjustmentForm, amount: parseFloat(e.target.value) || 0 })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={adjustmentForm.description}
                onChange={(e) => setAdjustmentForm({ ...adjustmentForm, description: e.target.value })}
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdjustmentOpen(false)}>Cancel</Button>
          <Button onClick={handleAdjustment} variant="contained" disabled={!adjustmentForm.description || !adjustmentForm.amount}>
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default TenantLedger;

